import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  IProjectSpaceTypeCategory,
  IProjectSpaceTypeSubcategory,
} from 'spw-redux/modules/projects/types';
import {
  currentProject as selectCurrentProject,
  selectCurrentUserIsViewOnly,
  selectProjectSpaceTypeCategories,
  selectProjectSpaceTypesByCategory,
} from 'spw-redux/modules/projects/selectors';
import {
  IProjectSpaceType,
  ISpaceTypeCategory,
  ISpaceTypeSubcategory,
} from 'spw-redux/modules/space_types/types';
import { Box, Divider, IconButton, Menu, MenuItem } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Row } from 'spw-shared/globalComponents';
import {
  selectAllChildWorksheetSpaces,
  selectCurrentScenario,
  selectWorksheet,
} from 'spw-redux/modules/calculators/selectors';
import { MoreHoriz } from '@mui/icons-material';
import { colors } from 'spw-theme';
import {
  IChildWorksheetSpaceType,
  IWorksheetRow,
} from 'spw-redux/modules/calculators/types';
import { FILE_CABINETS, LOCKERS } from 'spw-programming-projects/constants';
import { buildNewChildRow, buildNewRow } from 'spw-components/worksheet/utils';
import { postWorksheetRow } from 'spw-redux/modules/calculators/actions';
import {
  filterWorksheetRowsByCategory,
  useCategoryRowsBySpaceType,
  useDeleteChildRow,
  useDeleteWorksheetRow,
} from 'spw-programming-projects/building-details/worksheet/hooks';
import {
  defaultCategoryAndSubcategory,
  getCategoryOfSpaceType,
  isRowDirty,
} from 'spw-programming-projects/building-details/worksheet/utils';
import { selectClientFacingNamesAreVisible } from 'spw-redux/modules/space_types/selectors';
import { isIWCategoryOrSubcategory } from 'spw-projects/utils';
import {
  getIsFileCabinet,
  getIsIndividualWorkspace,
  getIsLocker,
  getIsLockerOrCabinet,
} from 'spw-shared/utils';
import { groupBy, isNil } from 'ramda';
import VisibilityButton from 'spw-programming-projects/building-details/worksheet/visibility-button';
import styled from 'styled-components';
import WorksheetSpaceTypeDialog from 'spw-components/dialogs/custom-space-types/worksheet-space-type-dialog';
import {
  addRowsToProjectWorksheets,
  fetchScenariosAssociatedWithSelectedRow,
  setRemoveSpaceTypeDialogStatus,
  setSelectedChildSpaceType,
  setSelectedSpaceType,
} from 'spw-redux/modules/worksheet/actions';
import {
  selectSelectedChildSpaceType,
  selectSelectedSpaceType,
  selectSelectedSpaceTypeCategory,
} from 'spw-redux/modules/worksheet/selectors';
import { sortRowsByPreviousId } from 'spw-redux/modules/calculators/utils';
import WorksheetRow from '../../../../models/WorksheetRow';
import { useSpaceTypeOptions } from './space-type-hooks';

const SpaceTypeRow = styled(Row)`
  position: relative;
  left: -48px;
  width: 100%;
  padding: 4px 24px 4px 48px;
  min-height: 24px;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  align-items: center;
  cursor: default;

  &:hover {
    background: ${colors.brightGray};
  }
  &.interactive:hover {
    cursor: pointer;
  }
`;

type Props = {
  category: IProjectSpaceTypeCategory | IProjectSpaceTypeSubcategory;
  setIsHideSpaceTypeConfirmationDialogOpen: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setIsEditSpaceTypeNameDialogOpen: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  selectParentSpaceType: (
    spaceTypeCategory: ISpaceTypeCategory,
    subcategory?: ISpaceTypeSubcategory
  ) => void;
};

type MoreButtonProps = {
  onClick: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
};

export const MoreButton = ({ onClick, disabled }: MoreButtonProps) => {
  return (
    <IconButton
      aria-label="more options"
      onClick={onClick}
      style={{ padding: 0, marginLeft: '4px' }}
      disabled={disabled}
    >
      <MoreHoriz htmlColor={colors.black} style={{ fontSize: '24px' }} />
    </IconButton>
  );
};

const SpaceTypeMenuItem = styled(MenuItem)`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`;

const CategorySpaceTypeList = ({
  category,
  setIsHideSpaceTypeConfirmationDialogOpen,
  setIsEditSpaceTypeNameDialogOpen,
  selectParentSpaceType,
}: Props) => {
  const dispatch = useDispatch();
  const {
    isCreateCustomSpaceTypeDialogOpen,
    setIsCreateCustomSpaceTypeDialogOpen,
    handleCreateCustomSpaceType,
    selectedParentSpaceType,
    isEditSpaceTypeNameDialogOpen,
  } = useSpaceTypeOptions();

  const onDismissDialog = () => {
    setIsCreateCustomSpaceTypeDialogOpen(false);
    setIsEditSpaceTypeNameDialogOpen(false);
  };
  const project = useSelector(selectCurrentProject);
  const spaceTypeCategories = useSelector(selectProjectSpaceTypeCategories);
  const currentScenario = useSelector(selectCurrentScenario);
  const worksheet = useSelector(selectWorksheet);
  const selectedSpaceType = useSelector(selectSelectedSpaceType);
  const selectedSpaceTypeCategory = useSelector(
    selectSelectedSpaceTypeCategory
  );
  const selectedChildSpaceType = useSelector(selectSelectedChildSpaceType);
  const spaceTypesGroupedByCategory = useSelector(
    selectProjectSpaceTypesByCategory
  );
  const currentUserIsViewOnly = useSelector(selectCurrentUserIsViewOnly);
  const spaceTypes: IProjectSpaceType[] = useMemo(
    () => spaceTypesGroupedByCategory[category.id] || [],
    [spaceTypesGroupedByCategory, category.id]
  );

  const clientFacingNamesAreVisible = useSelector(
    selectClientFacingNamesAreVisible
  );
  const [spaceTypeAnchorEl, setSpaceTypeAnchorEl] = useState(null);
  const [expandedSpaceTypes, setExpandedSpaceTypes] = useState<
    Record<number, boolean>
  >(
    spaceTypes.reduce((map, spaceType) => {
      return {
        ...map,
        [spaceType.id]: false,
      };
    }, {})
  );
  const deleteRow = useDeleteWorksheetRow();
  const deleteChildRow = useDeleteChildRow();

  const rowsBySpaceType: {
    [key: number]: WorksheetRow;
  } = useCategoryRowsBySpaceType(category);
  const childWorksheetSpaceTypes = useSelector(selectAllChildWorksheetSpaces);
  const childWorksheetSpaceTypesByParent: Record<
    string,
    IChildWorksheetSpaceType[]
  > = useMemo(
    () =>
      groupBy((childWorksheetSpaceType: IChildWorksheetSpaceType) =>
        childWorksheetSpaceType.spaceTypeId.toString()
      )(childWorksheetSpaceTypes),
    [childWorksheetSpaceTypes]
  );

  const childRowsByChildSpaceType: Record<number, WorksheetRow> = useMemo(
    () =>
      filterWorksheetRowsByCategory(worksheet, category)
        .filter((row: WorksheetRow) => row.child)
        ?.reduce((map, row: WorksheetRow) => {
          return {
            ...map,
            [row.spaceId]: row,
          };
        }, {}),
    [category, worksheet]
  );

  const refMap = useRef(
    spaceTypes.reduce(
      (
        map: Record<number, HTMLDivElement | null>,
        spaceType
      ): Record<number, HTMLDivElement | null> => {
        return {
          ...map,
          [spaceType.id]: null,
        };
      },
      {}
    )
  );

  const scrollToChildSpaceType = (spaceTypeId: number) => {
    if (refMap.current[spaceTypeId]) {
      refMap.current[spaceTypeId].scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  };

  const handleMoreButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    projectSpaceType: IProjectSpaceType
  ) => {
    event.stopPropagation();
    setSpaceTypeAnchorEl(event.currentTarget);
    dispatch(setRemoveSpaceTypeDialogStatus(false));
    dispatch(setSelectedChildSpaceType(null));
    dispatch(setSelectedSpaceType(projectSpaceType));
    dispatch(
      fetchScenariosAssociatedWithSelectedRow(
        project.id,
        projectSpaceType.spaceType.id,
        false
      )
    );
  };

  const handleMoreClickForChildSpaceType = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement>,
      childSpaceType: IChildWorksheetSpaceType
    ) => {
      event.stopPropagation();
      setSpaceTypeAnchorEl(event.currentTarget);
      dispatch(setRemoveSpaceTypeDialogStatus(false));
      dispatch(setSelectedSpaceType(null));
      dispatch(setSelectedChildSpaceType(childSpaceType));
      dispatch(
        fetchScenariosAssociatedWithSelectedRow(
          project.id,
          childSpaceType.id,
          true
        )
      );
    },
    [dispatch, project.id]
  );

  const handleOnCloseMenu = (e: React.BaseSyntheticEvent) => {
    e.stopPropagation();
    setSpaceTypeAnchorEl(null);
    dispatch(setSelectedSpaceType(null));
    dispatch(setSelectedChildSpaceType(null));
  };

  const setParentSpaceType = () => {
    const spaceTypeCategory =
      selectedSpaceTypeCategory ||
      getCategoryOfSpaceType(
        selectedSpaceType ?? selectedChildSpaceType,
        spaceTypeCategories
      );

    const {
      category: defaultCategory,
      subcategory,
    } = defaultCategoryAndSubcategory(spaceTypeCategory, spaceTypeCategories);

    selectParentSpaceType(defaultCategory, subcategory);
  };

  const handleCreateCustomSpaceTypeClick = () => {
    handleCreateCustomSpaceType();
    setParentSpaceType();
    setSpaceTypeAnchorEl(null);
  };

  const handleRenameSpaceTypeClick = () => {
    setIsEditSpaceTypeNameDialogOpen(true);
    setParentSpaceType();
    setSpaceTypeAnchorEl(null);
  };

  const handleHideFromAllWorksheets = () => {
    dispatch(setRemoveSpaceTypeDialogStatus(true, true));
    setSpaceTypeAnchorEl(null);
  };

  const handleDeleteSpaceType = () => {
    dispatch(setRemoveSpaceTypeDialogStatus(true, false));
    setSpaceTypeAnchorEl(null);
  };

  const handleShowOnAllWorksheets = () => {
    setSpaceTypeAnchorEl(null);
    dispatch(
      addRowsToProjectWorksheets(
        selectedSpaceType
          ? {
              projectSpaceTypeId: selectedSpaceType.id,
            }
          : {
              childWorksheetSpaceTypeId: selectedChildSpaceType.id,
            }
      )
    );
  };

  const handleHideSpaceTypeClick = (projectSpaceType: IProjectSpaceType) => {
    const row = rowsBySpaceType[projectSpaceType.id];
    let isSelectedRowDirty = false;
    if (
      (currentScenario.isDetailed &&
        isIWCategoryOrSubcategory(projectSpaceType.category)) ||
      getIsLockerOrCabinet(projectSpaceType.spaceType.spaceType)
    ) {
      const childSpaceTypes =
        childWorksheetSpaceTypesByParent[projectSpaceType.spaceType.id] || [];

      const childRows: WorksheetRow[] = childSpaceTypes
        .map(
          (childSpaceType: IChildWorksheetSpaceType) =>
            childRowsByChildSpaceType[childSpaceType.id]
        )
        .filter((childRow: WorksheetRow) => !isNil(childRow));

      isSelectedRowDirty = childRows.some(isRowDirty);
    } else {
      isSelectedRowDirty = isRowDirty(row);
    }

    if (isSelectedRowDirty) {
      setIsHideSpaceTypeConfirmationDialogOpen(true);
      dispatch(setSelectedSpaceType(projectSpaceType));
    } else {
      deleteRow(projectSpaceType);
    }
  };

  const handleShowSpaceTypeClick = (projectSpaceType: IProjectSpaceType) => {
    dispatch(postWorksheetRow(buildNewRow(projectSpaceType), worksheet.id));
  };

  const handleHideChildSpaceType = useCallback(
    (childWorksheetSpaceType: IChildWorksheetSpaceType) => {
      const childRow = childRowsByChildSpaceType[childWorksheetSpaceType.id];

      if (isRowDirty(childRow)) {
        setIsHideSpaceTypeConfirmationDialogOpen(true);
        dispatch(setSelectedChildSpaceType(childWorksheetSpaceType));
      } else {
        deleteChildRow(childWorksheetSpaceType);
      }
    },
    [
      childRowsByChildSpaceType,
      deleteChildRow,
      dispatch,
      setIsHideSpaceTypeConfirmationDialogOpen,
    ]
  );

  const handleShowChildSpaceType = useCallback(
    (childWorksheetSpaceType: IChildWorksheetSpaceType) => {
      const parentSpaceType = spaceTypes.find(
        (projectSpaceType) =>
          projectSpaceType.spaceType.id === childWorksheetSpaceType.parent.id
      );

      const childRow = buildNewChildRow(
        parentSpaceType,
        childWorksheetSpaceType
      );
      dispatch(
        postWorksheetRow(
          childRow,
          worksheet.id,
          !childWorksheetSpaceType.id,
          childWorksheetSpaceType.unitsHigh,
          childWorksheetSpaceType.name
        )
      );
    },
    [dispatch, spaceTypes, worksheet.id]
  );

  const handleSpaceTypeRowClick = (projectSpaceType: IProjectSpaceType) => {
    setExpandedSpaceTypes((prevState) => ({
      ...prevState,
      [projectSpaceType.id]: !prevState[projectSpaceType.id],
    }));
  };

  const isRenameOptionInMenu =
    (!isNil(selectedSpaceType) &&
      !getIsLockerOrCabinet(selectedSpaceType.spaceType.spaceType)) ||
    (!isNil(selectedChildSpaceType) &&
      !getIsLockerOrCabinet(selectedChildSpaceType.parent.spaceType));

  const isDeleteOptionInMenu =
    selectedSpaceType?.isCustom ||
    selectedSpaceType?.spaceType?.isCustom ||
    (selectedChildSpaceType &&
      !getIsLockerOrCabinet(selectedChildSpaceType.parent.spaceType));

  const sortedSpaceTypes = sortRowsByPreviousId(
    spaceTypes.map(
      (projectSpaceType: IProjectSpaceType) =>
        ({
          projectSpaceType,
          category: projectSpaceType.category,
          spaceType: projectSpaceType.spaceType,
        } as IWorksheetRow)
    )
  );

  useEffect(() => {
    spaceTypes.forEach((projectSpaceType) => {
      let allChildSpaceTypesHidden = true;
      const childSpaceTypes =
        childWorksheetSpaceTypesByParent[
          projectSpaceType.spaceType.id.toString()
        ];
      if (childSpaceTypes) {
        childSpaceTypes.forEach(
          (childWorksheetSpaceType: IChildWorksheetSpaceType) => {
            if (childRowsByChildSpaceType[childWorksheetSpaceType.id]) {
              allChildSpaceTypesHidden = false;
            }
          }
        );
      }

      const spaceTypeIsVisible = rowsBySpaceType[projectSpaceType.id];

      if (spaceTypeIsVisible && !allChildSpaceTypesHidden) {
        setExpandedSpaceTypes((prevState) => ({
          ...prevState,
          [projectSpaceType.id]: true,
        }));
      }
    });
  }, [
    spaceTypes,
    childRowsByChildSpaceType,
    childWorksheetSpaceTypesByParent,
    rowsBySpaceType,
  ]);

  const getChildRow = useCallback(
    (childWorksheetSpaceType: IChildWorksheetSpaceType) => (
      <SpaceTypeRow
        data-cy={`${childWorksheetSpaceType.name
          .replace(' ', '_')
          .trim()}_menu_item`}
        key={`${childWorksheetSpaceType.id}_${childWorksheetSpaceType.name}`}
      >
        <Box
          pl={3.5}
          flexGrow={1}
          style={{
            ...(isNil(childRowsByChildSpaceType[childWorksheetSpaceType.id])
              ? { color: colors.disabledIcons }
              : {}),
            wordBreak: 'break-all',
          }}
        >
          {clientFacingNamesAreVisible
            ? childWorksheetSpaceType.clientFacingName ||
              childWorksheetSpaceType.name
            : childWorksheetSpaceType.name}
        </Box>
        <Box display="flex">
          <VisibilityButton
            isOff={isNil(childRowsByChildSpaceType[childWorksheetSpaceType.id])}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              if (childRowsByChildSpaceType[childWorksheetSpaceType.id]) {
                handleHideChildSpaceType(childWorksheetSpaceType);
              } else {
                handleShowChildSpaceType(childWorksheetSpaceType);
              }
            }}
          />
        </Box>
        <Box display="flex" alignItems="center">
          <MoreButton
            disabled={currentUserIsViewOnly}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              handleMoreClickForChildSpaceType(event, childWorksheetSpaceType);
            }}
          />
        </Box>
      </SpaceTypeRow>
    ),
    [
      clientFacingNamesAreVisible,
      childRowsByChildSpaceType,
      currentUserIsViewOnly,
      handleHideChildSpaceType,
      handleMoreClickForChildSpaceType,
      handleShowChildSpaceType,
    ]
  );

  const getChildRows = useCallback(
    (projectSpaceType: IProjectSpaceType) => {
      if (!expandedSpaceTypes[projectSpaceType.id]) return null;
      const childSpaceTypes =
        childWorksheetSpaceTypesByParent[
          projectSpaceType.spaceType.id.toString()
        ] || [];
      if (getIsFileCabinet(projectSpaceType.spaceType.spaceType)) {
        const [lateralFileCabinets, verticalFileCabinets] = FILE_CABINETS.map(
          (cabinetsList) =>
            cabinetsList.map((cabinetName: string, index) => {
              const existingCabinetSpaceType = childSpaceTypes.find(
                ({ name }) => name === cabinetName
              );
              return (
                existingCabinetSpaceType || {
                  name: cabinetName,
                  unitsHigh: index + 1,
                  spaceTypeId: projectSpaceType.spaceType.id,
                  parent: {
                    ...projectSpaceType.spaceType,
                    spaceTypeCategory: projectSpaceType.category,
                  },
                }
              );
            })
        );
        return (
          <Box>
            {lateralFileCabinets?.map(
              (childWorksheetSpaceType: IChildWorksheetSpaceType) =>
                getChildRow(childWorksheetSpaceType)
            )}
            <Box style={{ padding: '8px 0px 8px 28px' }}>
              <Divider />
            </Box>
            {verticalFileCabinets?.map(
              (childWorksheetSpaceType: IChildWorksheetSpaceType) =>
                getChildRow(childWorksheetSpaceType)
            )}
          </Box>
        );
      }

      if (getIsLocker(projectSpaceType.spaceType.spaceType)) {
        const lockersList = LOCKERS.map((lockerName: string, index) => {
          const existingLockerSpaceType = childSpaceTypes.find(
            ({ name }) => name === lockerName
          );
          return (
            existingLockerSpaceType || {
              name: lockerName,
              unitsHigh: index + 1,
              spaceTypeId: projectSpaceType.spaceType.id,
              parent: {
                ...projectSpaceType.spaceType,
                spaceTypeCategory: projectSpaceType.category,
              },
            }
          );
        });
        return (
          <Box>
            {lockersList?.map(
              (childWorksheetSpaceType: IChildWorksheetSpaceType) =>
                getChildRow(childWorksheetSpaceType)
            )}
          </Box>
        );
      }
      return (
        <Box>
          {childSpaceTypes?.map(
            (childWorksheetSpaceType: IChildWorksheetSpaceType) =>
              getChildRow(childWorksheetSpaceType)
          )}
        </Box>
      );
    },
    [childWorksheetSpaceTypesByParent, expandedSpaceTypes, getChildRow]
  );

  return (
    <div>
      {sortedSpaceTypes.map(({ projectSpaceType }) => {
        return (currentScenario?.isDetailed &&
          isIWCategoryOrSubcategory(category)) ||
          getIsLockerOrCabinet(projectSpaceType.spaceType.spaceType) ? (
          <Box
            key={projectSpaceType.id}
            pb={0.5}
            style={{
              marginLeft: getIsLockerOrCabinet(
                projectSpaceType.spaceType.spaceType
              )
                ? '-28px'
                : '0',
            }}
            ref={(node: HTMLDivElement) => {
              if (projectSpaceType.id)
                refMap.current[projectSpaceType.id.toString()] = node;
            }}
          >
            <SpaceTypeRow
              data-cy={`${projectSpaceType.spaceType.name
                .replace(' ', '_')
                .trim()}_menu_item`}
              className="interactive"
              onClick={() => handleSpaceTypeRowClick(projectSpaceType)}
            >
              <Box mr={0.5} display="flex">
                {expandedSpaceTypes[projectSpaceType.id] ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </Box>
              <Box
                display="flex"
                alignItems="center"
                style={{
                  ...(isNil(rowsBySpaceType[projectSpaceType.id])
                    ? { color: colors.disabledIcons }
                    : {}),
                  wordBreak: 'break-all',
                }}
              >
                {clientFacingNamesAreVisible &&
                projectSpaceType?.clientFacingName
                  ? projectSpaceType?.clientFacingName
                  : projectSpaceType.spaceType.name}
              </Box>
              <Box
                display="flex"
                flexGrow={1}
                alignItems="center"
                justifyContent="flex-end"
              >
                <VisibilityButton
                  isOff={isNil(rowsBySpaceType[projectSpaceType.id])}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (rowsBySpaceType[projectSpaceType.id]) {
                      handleHideSpaceTypeClick(projectSpaceType);
                    } else {
                      handleShowSpaceTypeClick(projectSpaceType);
                    }
                  }}
                />
              </Box>
              <Box display="flex" alignItems="center">
                <MoreButton
                  disabled={currentUserIsViewOnly}
                  data-testid={`${projectSpaceType.spaceType.name
                    .replace(' ', '_')
                    .trim()}_actions_menu`}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    handleMoreButtonClick(event, projectSpaceType);
                  }}
                />
              </Box>
            </SpaceTypeRow>
            {getChildRows(projectSpaceType)}
          </Box>
        ) : (
          <Box key={projectSpaceType.id} pb={0.5}>
            <SpaceTypeRow
              data-cy={`${projectSpaceType.spaceType.name
                .replace(' ', '_')
                .trim()}_menu_item`}
              key={projectSpaceType.id}
            >
              <Box
                display="flex"
                alignItems="center"
                flexGrow={1}
                style={{
                  ...(isNil(rowsBySpaceType[projectSpaceType.id])
                    ? { color: colors.disabledIcons }
                    : {}),
                  wordBreak: 'break-all',
                }}
              >
                {clientFacingNamesAreVisible &&
                projectSpaceType?.clientFacingName
                  ? projectSpaceType?.clientFacingName
                  : projectSpaceType.spaceType.name}
              </Box>
              <Box display="flex" alignItems="center">
                <VisibilityButton
                  isOff={isNil(rowsBySpaceType[projectSpaceType.id])}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (rowsBySpaceType[projectSpaceType.id]) {
                      handleHideSpaceTypeClick(projectSpaceType);
                    } else {
                      handleShowSpaceTypeClick(projectSpaceType);
                    }
                  }}
                />
              </Box>
              <Box display="flex" alignItems="center">
                <MoreButton
                  disabled={currentUserIsViewOnly}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    handleMoreButtonClick(event, projectSpaceType);
                  }}
                />
              </Box>
            </SpaceTypeRow>
          </Box>
        );
      })}
      <Menu
        id="space-type-menu"
        anchorEl={spaceTypeAnchorEl}
        open={Boolean(spaceTypeAnchorEl)}
        onClose={handleOnCloseMenu}
        disableScrollLock
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {isRenameOptionInMenu && (
          <SpaceTypeMenuItem
            key="rename-space-type"
            onClick={handleRenameSpaceTypeClick}
          >
            Rename Space Type
          </SpaceTypeMenuItem>
        )}
        {selectedSpaceType &&
        (getIsIndividualWorkspace(selectedSpaceType.spaceType.spaceType) ||
          selectedSpaceType.spaceType.isCustom) &&
        currentScenario.isDetailed ? (
          <SpaceTypeMenuItem
            key="create-custom-space-type"
            onClick={handleCreateCustomSpaceTypeClick}
          >
            Create Custom Space Type
          </SpaceTypeMenuItem>
        ) : null}
        {(!isNil(rowsBySpaceType[selectedSpaceType?.id]) ||
          !isNil(childRowsByChildSpaceType[selectedChildSpaceType?.id])) && (
          <SpaceTypeMenuItem
            key="hide-from-all-worksheets"
            onClick={handleHideFromAllWorksheets}
          >
            Hide on All Worksheets
          </SpaceTypeMenuItem>
        )}
        {isNil(rowsBySpaceType[selectedSpaceType?.id]) &&
          isNil(childRowsByChildSpaceType[selectedChildSpaceType?.id]) && (
            <SpaceTypeMenuItem onClick={handleShowOnAllWorksheets}>
              Show on All Worksheets
            </SpaceTypeMenuItem>
          )}
        {isDeleteOptionInMenu && (
          <SpaceTypeMenuItem
            key="delete-space-type"
            onClick={handleDeleteSpaceType}
          >
            Delete Space Type
          </SpaceTypeMenuItem>
        )}
      </Menu>
      {(isCreateCustomSpaceTypeDialogOpen || isEditSpaceTypeNameDialogOpen) && (
        <WorksheetSpaceTypeDialog
          isOpen={
            isCreateCustomSpaceTypeDialogOpen || isEditSpaceTypeNameDialogOpen
          }
          onDismiss={onDismissDialog}
          isEditingSpaceType={isEditSpaceTypeNameDialogOpen}
          parentSpaceType={selectedParentSpaceType}
          onCreate={({ id }) => {
            setExpandedSpaceTypes((prevState) => ({
              ...prevState,
              [id]: true,
            }));
            setTimeout(() => scrollToChildSpaceType(id), 100);
          }}
        />
      )}
    </div>
  );
};

export default CategorySpaceTypeList;
